import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as OpenSVG } from "go-core/images/svg/actions/link.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { OrganizationApi, UserRole } from "../../../../../../services/Api/types";
import { getLicenseExpirationDays } from "../../../../../../services/license";
import FormatSubscriptionLicenseType from "../../../FormatSubscriptionLicenseType/FormatSubscriptionLicenseType";
import { OrganizationPreviewRequests } from "../services/types";

interface Props {
	organization: OrganizationApi;
	requests: OrganizationPreviewRequests;
	refresh: () => Promise<void>;
	url: string;
	areAllowed: (contexts: Record<string, UserRole[]>) => boolean;
}

const OrganizationLicenseCard = ({ organization, requests, refresh, url, areAllowed }: Props) => {
	const [showMore, setShowMore] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const confirmation = useConfirmation();
	const { t } = useTranslation("admin");
	const extendPaymentDateAction = requests.extendPaymentDate;

	const extendPaymentDate = async () => {
		if (!extendPaymentDateAction) return;
		setLoading(true);

		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		try {
			await extendPaymentDateAction(organization.id);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			await refresh();
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		setLoading(false);
	};
	const expirationDays = getLicenseExpirationDays(organization.license_expires_at, organization.timezone);
	const blockedDays = getLicenseExpirationDays(organization.license_blocked_at, organization.timezone);
	const paymentDueDays = getLicenseExpirationDays(organization.payment_date_due, organization.timezone);
	// const paymentDueDays = 0;
	const lastExtendPaymentDate = organization.expected_payment_date;

	const renderLicenseByType = () => {
		if (organization.license_type === "CANCELLED") {
			return (
				<>
					<div className={"alert alert-danger"}>
						Licencja anulowana od {FormatDate(organization.license_expires_at)}
					</div>
				</>
			);
		}
		if (organization.license_type === "NONE") {
			return (
				<>
					<div className={"alert alert-danger"}>Brak licencji</div>
				</>
			);
		}
		if (organization.license_type === "TRIAL") {
			return (
				<>
					<div className={"alert alert-warning"}>Licencja trial</div>

					{organization.license_expires_at && (
						<div className="card-body-item">
							<div className="item-info">
								<span className="label">Data zakończenia trial</span>
								<span className={expirationDays <= 0 ? "text-danger" : ""}>
									{FormatDate(organization.license_expires_at)}
								</span>
							</div>
						</div>
					)}
				</>
			);
		}
		if (organization.license_type === "MANUAL") {
			return (
				<>
					<div className={"alert alert-warning"}>Licencja ręczna</div>
				</>
			);
		}

		const drawNotPaidInvoices = () => {
			let result = FormatMoney({
				amount: organization.not_paid_invoices_amount?.amount ?? 0,
				currency: organization.not_paid_invoices_amount?.currency ?? "PLN",
			});
			if (organization.not_paid_invoices_count) {
				result += ` / ${t("modules.organization.field.invoices.title", {
					count: organization.not_paid_invoices_count ?? 0,
				})}`;
			}
			return result;
		};
		return (
			<>
				{organization.license_type === "PAUSED" && (
					<div className={"alert alert-warning"}>Licencja zatrzymana</div>
				)}
				{organization.license_type === "FUTURE" && (
					<div className={"alert alert-warning"}>Licencja przyszła</div>
				)}
				{organization.license_expires_at && (
					<div className="card-body-item">
						<div className="item-info">
							<span className="label">{t("modules.organization.field.license_expires_at.title")}</span>
							<span className={expirationDays <= 0 ? "text-danger" : ""}>
								{FormatDate(organization.license_expires_at, organization.timezone)}
							</span>
						</div>
					</div>
				)}
				{organization.license_blocked_at && (
					<div className="card-body-item">
						<div className="item-info">
							<span className="label">Data blokady</span>
							<span className={blockedDays <= 0 ? "text-danger" : ""}>
								{FormatDate(organization.license_blocked_at, organization.timezone)}
							</span>
						</div>
					</div>
				)}
				{!!(organization.payment_date_due || organization.not_paid_invoices_count) && (
					<Link
						to={`${url}/invoices`}
						className={"card-body-item justify-content-between align-items-center"}
					>
						<div className="d-flex align-items-center">
							<div className="item-info">
								<span className="label">{t("common.word.not_paid_invoices")}</span>
								<span className={paymentDueDays <= 0 ? "text-danger" : ""}>
									{drawNotPaidInvoices()}
								</span>
								{organization.payment_date_due && (
									<div>
										<small
											className={`d-flex ${paymentDueDays <= 0 ? "text-danger" : "text-muted"}`}
										>
											Termin:{` `}
											{FormatDate(organization.payment_date_due)}
										</small>
									</div>
								)}
							</div>
						</div>
						<OpenSVG className="me-0" />
					</Link>
				)}

				{lastExtendPaymentDate ? (
					<div className={"alert alert-warning"}>Licencja była już raz przedłużona!</div>
				) : (
					<>
						{areAllowed({
							PARTNER: [UserRole.SUPPORT, UserRole.ADMIN, UserRole.OFFICE, UserRole.SALE],
							SUPPORT: [UserRole.SUPPORT, UserRole.ADMIN, UserRole.OFFICE, UserRole.SALE],
							ADMIN: [],
						}) &&
							extendPaymentDateAction &&
							expirationDays < 4 && (
								<>
									<ButtonLoading
										loading={loading}
										className={"btn btn-primary w-100"}
										onClick={extendPaymentDate}
									>
										Odblokuj na 4 dni
									</ButtonLoading>
								</>
							)}
					</>
				)}
			</>
		);
	};
	return (
		<>
			<Card>
				<Card.Body>
					<h5 className={"d-flex justify-content-between"}>
						Licencja
						<FormatSubscriptionLicenseType license_type={organization.license_type} />
					</h5>
					{renderLicenseByType()}
				</Card.Body>
			</Card>
		</>
	);
};

export default OrganizationLicenseCard;
